import { LitElement, html, TemplateResult, css, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';
import { Task } from '@lit/task';
import { localized } from '@lit/localize';
import mainStyles from './styles/main.scss?inline';
import { updateLocale } from './localization/config';
import { DestinationFaq, FaqGroup } from './types/faq.types';
import { FaqService } from './services/faq-service';
import { setInitialUri, triggerSchemaJsonEvent } from './services/schemaOrgJsonLd.service';
import { LocaleCode } from './types/localCodes';

/**
 * DERTOUR FAQ Module
 */
@localized()
export class Faq extends LitElement {
    static styles = [
        css`
            ${unsafeCSS(mainStyles)}
        `,
    ];

    @property({ type: String })
    lang = LocaleCode.DE_CH;

    @property({ type: String })
    currency = '';

    @property({ type: String })
    public brand = '';

    @property({ type: String })
    public tenant = 'kuoni';

    @property({ type: Object, attribute: 'additional-tracking-data' })
    public additionalTrackingData = {};

    @property({ attribute: 'identifier' })
    identifier = '';

    @property({ attribute: 'travelcontext' })
    travelcontext?: string;

    @state()
    destinationFaq: DestinationFaq | undefined;

    private _contentTask: Task<readonly [string, string, string, string, string, string], DestinationFaq> | null = null;

    /**
     * Initialize the component
     */
    async connectedCallback(): Promise<void> {
        super.connectedCallback();

        // 1. Set locale based on lang attribute (wait for it to load)
        const langAttribute = this.lang ?? 'de-CH';
        await updateLocale(langAttribute);

        const taskDeps = () => {
            const deps = [
                this.brand,
                this.tenant,
                this.currency,
                this.lang ?? 'de-CH',
                this.identifier,
                this.travelcontext ?? 'DESTINATIONS',
            ] as const;
            return deps;
        };

        this._contentTask = new Task<readonly [string, string, string, string, string, string], DestinationFaq>(
            this,
            async ([brand, tenant, currency, lang, identifier, travelcontext]) => {
                return await FaqService.fetchFaq({
                    brand,
                    tenant,
                    currency,
                    lang,
                    identifier,
                    travelcontext,
                });
            },
            taskDeps
        );
    }

    /**
     * Update lifecycle - watch for brand and language changes at runtime
     * @param changedProperties - Map of changed properties with their previous values
     */
    updated(changedProperties: Map<string, unknown>): void {
        super.updated(changedProperties);

        // Handle language changes at runtime
        if (changedProperties.has('lang')) {
            const newLang = this.lang ?? 'de-CH';
            updateLocale(newLang).then(() => {
                this.requestUpdate();
            });
        }
    }

    private createKeyValuesFromGroup(group: FaqGroup): Record<string, string> {
        const keyValues: Record<string, string> = {};

        group.faqs
            .filter((faq) => faq.isActive)
            .sort((a, b) => a.order - b.order)
            .forEach((faq) => {
                keyValues[faq.question] = faq.answer;
            });

        return keyValues;
    }

    private renderFaqGroups(groups: FaqGroup[]): TemplateResult[] {
        return groups
            .sort((a, b) => a.groupOrder - b.groupOrder)
            .map((group) => {
                const keyValues = this.createKeyValuesFromGroup(group);

                triggerSchemaJsonEvent(
                    {
                        uri: setInitialUri(this.identifier),
                        data: keyValues,
                    },
                    this
                );

                return html`
                    <section class="Faq-group">
                        <der-ui-accordion
                            .keyValues=${keyValues}
                            .firstOpen=${false}
                            .onlyOneOpen=${true}
                            .noPadding=${true}
                        ></der-ui-accordion>
                    </section>
                `;
            });
    }

    /**
     * Emit a warning event
     * @param name - The warning name
     * @param data - Additional warning data
     */
    protected warn(name: string, data: object): void {
        const detail: { name: string; data: object } = { name: name.toLowerCase(), data };

        this.dispatchEvent(new CustomEvent('der.warning', { detail, bubbles: true, composed: true }));
    }

    /**
     * Emit an error event
     * @param name - The error name
     * @param data - Additional error data
     */
    error(name: string, data: object): void {
        const detail: { name: string; data: object } = { name: name.toLowerCase(), data };

        this.dispatchEvent(new CustomEvent('der.error', { detail, bubbles: true, composed: true }));
    }

    /**
     * Render the FAQ module
     * @returns Template result
     */
    protected render(): TemplateResult {
        if (!this._contentTask) {
            return html``;
        }

        return (
            this._contentTask.render({
                pending: () => html``,
                complete: (destinationFaq: DestinationFaq) => {
                    const hasAnyFaqs = destinationFaq?.faqGroups?.some((g) => g.faqs?.length > 0) ?? false;
                    if (!destinationFaq || !destinationFaq.faqGroups || !hasAnyFaqs) {
                        const warnData = {
                            message: 'No FAQ data available',
                            identifier: this.identifier,
                            travelcontext: this.travelcontext,
                        };
                        console.warn('[der-tm-faq]', warnData);
                        this.warn(this.tagName, warnData);
                        return html``;
                    }

                    const firstGroup = destinationFaq.faqGroups[0];

                    return html`
                        <div class="der-tm-faq-module">
                            <div class="der-tm-faq-header">
                                <der-ui-module-title
                                    title=${destinationFaq.title ?? firstGroup.groupTitle ?? ''}
                                    intro=${destinationFaq.intro ?? firstGroup.intro ?? ''}
                                ></der-ui-module-title>
                            </div>
                            ${this.renderFaqGroups(destinationFaq.faqGroups)}
                        </div>
                    `;
                },
                error: () => {
                    this.warn(this.tagName, {
                        message: 'Error loading FAQ data',
                        identifier: this.identifier,
                        travelcontext: this.travelcontext,
                    });
                    return html``;
                },
            }) ?? html``
        );
    }
}

if (!customElements.get('der-tm-faq')) {
    customElements.define('der-tm-faq', Faq);
}
